import { Navigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@contexts/AuthContext';

/**
 * ProtectedRoute
 * Wraps a route that requires authentication and optionally specific roles.
 *
 * @param {string[]} roles - Allowed roles. If empty, any authenticated user passes.
 * @param {string}   platformId - If set, also checks platform-level access.
 */
export function ProtectedRoute({ children, roles = [], platformId = null }) {
  const { isAuthenticated, isLoading, hasRole, hasPlatformAccess } = useAuth();
  const location = useLocation();

  // Still restoring session
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gl-void flex items-center justify-center">
        <motion.div
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="flex flex-col items-center gap-3"
        >
          <div
            className="w-8 h-8 rounded-lg"
            style={{ background: 'linear-gradient(135deg, #2D7D46, #1A5E9E)' }}
          />
          <span className="text-xs font-mono text-gl-muted uppercase tracking-widest">Authenticating</span>
        </motion.div>
      </div>
    );
  }

  // Not logged in → redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Role check
  if (roles.length > 0 && !hasRole(roles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Platform-level access check
  if (platformId && !hasPlatformAccess(platformId)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}

/** Unauthorized page */
export function Unauthorized() {
  return (
    <div className="min-h-screen bg-gl-void flex items-center justify-center px-4">
      <div className="text-center">
        <p className="text-6xl font-display font-black text-gl-ghost mb-2">403</p>
        <p className="text-gl-muted text-sm">You don't have access to this area.</p>
        <a href="/" className="mt-6 inline-block text-xs font-mono text-gl-teal underline">
          ← Return to hub
        </a>
      </div>
    </div>
  );
}
